import React, { useMemo } from 'react';
import { Paper, Typography } from '@mui/material';
import { ResponsiveContainer, BarChart, XAxis, YAxis, Tooltip, Bar, Cell } from 'recharts';
import { ObjectModel } from '../hooks/HObjects';
import { getFilteredYieldGross } from '../hooks/HData';

export const YieldComparisonChart: React.FC<{
    objectData?: ObjectModel | null;
    tableData?: any[];
}> = ({ objectData, tableData = [] }) => {
    const objectYieldGross = useMemo(() => {
        const rawValue = (objectData as any)?.yield_gross;
        if (rawValue === null || rawValue === undefined || rawValue === '') return null;
        const sanitized = String(rawValue).replace('%', '').trim();
        const parsed = Number(sanitized);
        return Number.isFinite(parsed) ? parsed : null;
    }, [objectData]);

    const yieldComparisonData = useMemo(() => {
        const rows: Array<{ label: string; value: number }> = [];

        if (objectData) {
            const typeValue = String((objectData as any)?.type_ ?? '').trim();
            const countryValue = String((objectData as any)?.country ?? '').trim();

            if (typeValue && countryValue) {
                const yields = getFilteredYieldGross(tableData as any[], typeValue, countryValue);
                if (yields.length > 0) {
                    const avg = Number((yields.reduce((s, v) => s + v, 0) / yields.length).toFixed(2));
                    const mx = Number(Math.max(...yields).toFixed(2));
                    const mn = Number(Math.min(...yields).toFixed(2));
                    rows.push({ label: 'Sector Avg', value: avg });
                    rows.push({ label: 'Sector Max', value: mx });
                    rows.push({ label: 'Sector Min', value: mn });
                }
            }
        }

        if (objectYieldGross !== null) {
            rows.push({ label: 'Object Yield', value: objectYieldGross });
        }

        return rows;
    }, [tableData, objectData, objectYieldGross]);

    if (!yieldComparisonData || yieldComparisonData.length === 0) {
        return (
            <Paper className="object-details-paper">
                <Typography variant="h6" className="object-details-paper-title">Yield Comparison</Typography>
                <div className="object-details-empty">No yield data available for comparison.</div>
            </Paper>
        );
    }

    return (
        <Paper className="object-details-paper">
            <Typography variant="h6" className="object-details-paper-title">Yield Comparison</Typography>
            <div className="object-details-yield-chart">
                <ResponsiveContainer width="100%" height={170}>
                    <BarChart data={yieldComparisonData} layout="vertical" margin={{ top: 10, right: 20, left: 12, bottom: 10 }}>
                        <XAxis type="number" tickFormatter={(value) => `${value}%`} />
                        <YAxis type="category" dataKey="label" width={170} />
                        <Tooltip formatter={(value: any) => `${value}%`} />
                        <Bar dataKey="value" name="Yield" radius={[0, 6, 6, 0]} isAnimationActive={false}>
                            {yieldComparisonData.map((entry, idx) => {
                                let fill = '#1976d2';
                                if (entry.label === 'Sector Max') fill = '#2e7d32';
                                if (entry.label === 'Sector Min') fill = '#c62828';
                                if (entry.label === 'Object Yield') fill = '#1928d2';
                                return <Cell key={`cell-${idx}`} fill={fill} />;
                            })}
                        </Bar>
                    </BarChart>
                </ResponsiveContainer>
            </div>
        </Paper>
    );
};

export default YieldComparisonChart;
